import { X } from "lucide-react";
import { useEffect, useRef } from "react";
import { useIntl } from "react-intl";

import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { AllResults } from "./AllResults";
import { SearchLoading } from "./SearchLoading";

import { generateRouteByCaseId, useOrganizationParams } from "~/helpers";
import { trackEvent } from "~/utils/analytics";
import { useSearch } from "../hooks/useSearch";
import { SearchEmpty } from "./SearchEmpty";

type SearchWithShortcutProps = {
  setIsOpen: (open: boolean) => void;
};

export const SearchWithShortcut: React.FC<SearchWithShortcutProps> = ({
  setIsOpen,
}) => {
  const intl = useIntl();
  const modalRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const { organizationId } = useOrganizationParams();

  const { search, setSearch, clearSearch, data, isLoading } = useSearch({
    organizationId,
  });

  useEffect(() => {
    if (isLoading) {
      trackEvent({ eventName: "app/search_modal/loading" });
    }
  }, [isLoading]);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.code === "Escape") {
      event.preventDefault();
      setIsOpen(false);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      trackEvent({ eventName: "app/search_modal/background.overlay/click" });
      setIsOpen(false);
    } else {
      trackEvent({ eventName: "app/search_modal/search.input/click" });
    }
  };

  const goToCase = (caseId: string) => {
    const caseUrl = generateRouteByCaseId(organizationId, caseId);
    window.open(caseUrl, "_blank");
  };

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);
    document.body.classList.add("overflow-hidden");

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.classList.remove("overflow-hidden");
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black/10 flex justify-center items-start pt-[180px]">
      <div
        ref={modalRef}
        className="bg-white px-3 pt-3 rounded-xl shadow w-[664px]"
      >
        <div className={`relative pb-3 ${search && "border-b"}`}>
          <Input
            placeholder={intl.formatMessage({
              id: "search.search",
              defaultMessage: "Search",
            })}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            ref={searchInputRef}
            className="bg-white px-1 py-2 border-0"
          />
          {search && (
            <Button
              variant="secondary"
              size="icon"
              className="absolute right-0 top-0"
              onClick={clearSearch}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {isLoading ? (
          <SearchLoading />
        ) : (
          search &&
          (data && (data.cases?.length > 0 || data.documents?.length > 0) ? (
            <AllResults data={data} goToCase={goToCase} query={search} />
          ) : (
            <SearchEmpty />
          ))
        )}
      </div>
    </div>
  );
};
